import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Download, MonitorSmartphone } from 'lucide-react';

import { APP_VERSION } from '@/utils/version';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';

type ReleaseRow = {
  id: string;
  version: string;
  notes: string | null;
  min_supported_version: string | null;
  force_update: boolean | null;
  platform: string | null;
  created_at: string | null;
};

const compareVersions = (a: string, b: string) => {
  const left = a.split('.').map((value) => Number.parseInt(value, 10) || 0);
  const right = b.split('.').map((value) => Number.parseInt(value, 10) || 0);
  const length = Math.max(left.length, right.length);

  for (let index = 0; index < length; index += 1) {
    const l = left[index] ?? 0;
    const r = right[index] ?? 0;
    if (l > r) return 1;
    if (l < r) return -1;
  }

  return 0;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

export default function MapleOSUpdates() {
  const { toast } = useToast();
  const [latestRelease, setLatestRelease] = useState<ReleaseRow | null>(null);
  const [history, setHistory] = useState<ReleaseRow[]>([]);
  const [checking, setChecking] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [hasSwUpdate, setHasSwUpdate] = useState(false);

  const isElectron =
    typeof window !== 'undefined' &&
    typeof window.process === 'object' &&
    !!window.process.versions?.electron;

  const fetchReleases = useCallback(async () => {
    const { data, error } = await supabase
      .from('app_releases')
      .select('id, version, notes, min_supported_version, force_update, platform, created_at')
      .eq('platform', 'web')
      .order('created_at', { ascending: false })
      .limit(12);

    if (error) {
      console.error('[MapleOSUpdates] Failed to fetch releases', error);
      toast({ title: 'Could not load updates', description: 'Please try again later.', variant: 'destructive' });
      return [] as ReleaseRow[];
    }

    const releases = (data || []) as ReleaseRow[];
    setLatestRelease(releases[0] || null);
    setHistory(releases);
    return releases;
  }, [toast]);

  const checkServiceWorkerUpdate = useCallback(async () => {
    if (isElectron || !("serviceWorker" in navigator)) {
      setHasSwUpdate(false);
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.update();

    const controller = navigator.serviceWorker.controller;
    if (!controller) {
      setHasSwUpdate(false);
      return false;
    }

    const channel = new MessageChannel();
    const result = await new Promise<boolean>((resolve) => {
      channel.port1.onmessage = (event) => {
        const updateAvailable = event?.data?.hasUpdate === true;
        setHasSwUpdate(updateAvailable);
        resolve(updateAvailable);
      };
      controller.postMessage({ type: 'CHECK_FOR_UPDATE' }, [channel.port2]);
    });

    return result;
  }, [isElectron]);

  const forceUpdateRequired = useMemo(() => {
    if (!latestRelease) return false;
    if (latestRelease.force_update) return true;
    if (latestRelease.min_supported_version && compareVersions(APP_VERSION, latestRelease.min_supported_version) < 0) {
      return true;
    }
    return false;
  }, [latestRelease]);

  const statusText = useMemo(() => {
    if (!latestRelease) return 'Checking…';
    if (forceUpdateRequired) return 'Update required';
    if (compareVersions(latestRelease.version, APP_VERSION) > 0 || hasSwUpdate) return 'Update available';
    return 'Up to date';
  }, [forceUpdateRequired, hasSwUpdate, latestRelease]);

  const checkForUpdates = useCallback(async () => {
    setChecking(true);
    try {
      const releases = await fetchReleases();
      const updateAvailable = await checkServiceWorkerUpdate();
      const latest = releases[0];

      toast({
        title: 'Update check complete',
        description: latest
          ? `Installed ${APP_VERSION} • Latest ${latest.version} • ${updateAvailable || compareVersions(latest.version, APP_VERSION) > 0 ? 'Update available' : 'Up to date'}`
          : `Installed ${APP_VERSION}`,
      });
    } finally {
      setChecking(false);
    }
  }, [APP_VERSION, checkServiceWorkerUpdate, fetchReleases, toast]);

  const installNow = useCallback(async () => {
    if (isElectron) {
      toast({
        title: 'Desktop updates are handled separately',
        description: 'Electron builds follow a different update flow than the web app.',
      });
      return;
    }

    setInstalling(true);
    try {
      if (!("serviceWorker" in navigator)) {
        window.location.reload();
        return;
      }

      const controller = navigator.serviceWorker.controller;
      if (!controller) {
        window.location.reload();
        return;
      }

      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          window.location.reload();
        },
        { once: true }
      );

      controller.postMessage({ type: 'SKIP_WAITING' });
    } finally {
      setInstalling(false);
    }
  }, [isElectron, toast]);

  useEffect(() => {
    void fetchReleases();
  }, [fetchReleases]);

  return (
    <div className="h-full overflow-auto bg-[#F4F7FB] p-4 text-slate-900">
      <div className="mx-auto max-w-5xl space-y-4">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b bg-white/80">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <CardTitle className="text-2xl">MapleOS Updates</CardTitle>
                <CardDescription>
                  Check for new MapleOS releases, install updates, and review the latest release notes.
                </CardDescription>
              </div>
              <Badge variant={statusText === 'Up to date' ? 'secondary' : 'default'}>{statusText}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 bg-white p-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border bg-slate-50 p-4">
                <div className="text-sm text-slate-500">Installed version</div>
                <div className="mt-2 text-xl font-semibold">{APP_VERSION}</div>
              </div>
              <div className="rounded-xl border bg-slate-50 p-4">
                <div className="text-sm text-slate-500">Latest version</div>
                <div className="mt-2 text-xl font-semibold">{latestRelease?.version || '—'}</div>
              </div>
              <div className="rounded-xl border bg-slate-50 p-4">
                <div className="text-sm text-slate-500">Platform</div>
                <div className="mt-2 text-xl font-semibold">{isElectron ? 'Desktop (Electron)' : 'Web (PWA)'}</div>
              </div>
              <div className="rounded-xl border bg-slate-50 p-4">
                <div className="text-sm text-slate-500">Latest published</div>
                <div className="mt-2 text-xl font-semibold">{formatDate(latestRelease?.created_at)}</div>
              </div>
            </div>

            {isElectron ? (
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <MonitorSmartphone className="mt-0.5 h-4 w-4" />
                <p>Electron builds handle updates differently. You can still review the latest MapleOS release information here.</p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => void checkForUpdates()} disabled={checking}>
                <RefreshCw className={`mr-2 h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
                {checking ? 'Checking…' : 'Check for updates'}
              </Button>
              <Button onClick={() => void installNow()} disabled={installing || isElectron}>
                <Download className="mr-2 h-4 w-4" />
                {installing ? 'Installing…' : 'Install now'}
              </Button>
            </div>

            <div className="rounded-xl border bg-slate-50 p-4">
              <div className="mb-2 text-sm font-medium text-slate-700">Release notes</div>
              <div className="whitespace-pre-line text-sm text-slate-600">
                {latestRelease?.notes || 'No release notes published for the latest version.'}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>Release history</CardTitle>
            <CardDescription>Latest MapleOS web releases from app_releases.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {history.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                No release history available.
              </div>
            ) : null}
            {history.map((release) => (
              <div key={release.id} className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold">Version {release.version}</div>
                    <div className="text-sm text-muted-foreground">{formatDate(release.created_at)}</div>
                  </div>
                  {release.force_update ? <Badge>Required</Badge> : <Badge variant="secondary">Optional</Badge>}
                </div>
                <p className="mt-3 whitespace-pre-line text-sm text-slate-600">
                  {release.notes || 'No notes provided.'}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
